// ── SIE-PARSER ────────────────────────────────────────────────────
// Läser SIE4-filer (export från Fortnox m.fl.) helt i webbläsaren.
// Ger månadssaldon per konto × kostnadsställe ur #VER/#TRANS-raderna
// (#RES/#UB är årssaldon och räcker inte för månadsbudgetering).
//
// SIE-formatet är radbaserat, CP437-kodat ("PC8"), med citerade fält
// och dimensionslistor i klammerparenteser:
//   #TRANS 4010 {1 "100"} 12345.67 20241105 "text"
// Dimension 1 = kostnadsställe (Fortnox-standard).

// CP437 → Unicode för svenska/nordiska tecken (resten är ASCII).
const CP437 = {
  0x84: 'ä', 0x86: 'å', 0x94: 'ö', 0x8e: 'Ä', 0x8f: 'Å', 0x99: 'Ö',
  0x82: 'é', 0x90: 'É', 0x81: 'ü', 0x9a: 'Ü', 0x89: 'ë', 0x8b: 'ï',
  0xe1: 'ß', 0x87: 'ç', 0xa0: 'á', 0xa1: 'í', 0xa2: 'ó', 0xa3: 'ú',
};
export function dekodaCp437(buffer) {
  const bytes = new Uint8Array(buffer);
  let ut = '';
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    ut += b < 0x80 ? String.fromCharCode(b) : (CP437[b] || '?');
  }
  return ut;
}

// Tokenisera en SIE-rad: whitespace-separerade fält, "citerade fält",
// och {dimensionslistor} som ett token (utan klamrarna).
function tokenisera(rad) {
  const tokens = [];
  let i = 0;
  const n = rad.length;
  while (i < n) {
    while (i < n && (rad[i] === ' ' || rad[i] === '\t')) i++;
    if (i >= n) break;
    if (rad[i] === '"') {
      let s = ''; i++;
      while (i < n && rad[i] !== '"') { s += rad[i] === '\\' && rad[i + 1] === '"' ? rad[++i] : rad[i]; i++; }
      i++; tokens.push({ typ: 'str', v: s });
    } else if (rad[i] === '{') {
      let s = ''; i++;
      while (i < n && rad[i] !== '}') { s += rad[i]; i++; }
      i++; tokens.push({ typ: 'dim', v: s.trim() });
    } else {
      let s = '';
      while (i < n && rad[i] !== ' ' && rad[i] !== '\t') { s += rad[i]; i++; }
      tokens.push({ typ: 'ord', v: s });
    }
  }
  return tokens;
}

// Kostnadsställe ur en dimensionslista: "1 \"100\"" → "100"
function ksAvDim(dimStr) {
  if (!dimStr) return '';
  const t = tokenisera(dimStr);
  for (let i = 0; i + 1 < t.length; i += 2) {
    if (t[i].v === '1') return t[i + 1].v;
  }
  return '';
}

/**
 * Parsa en SIE-fil (text). Returnerar:
 * {
 *   foretag, orgnr,
 *   rar: [{ ar: 0|-1|…, start: 'YYYYMMDD', slut: 'YYYYMMDD' }],
 *   konton: { '4010': 'Inköp varor' },
 *   ks: { '100': 'Hotell' },
 *   manader: { 'YYYYMM': { 'konto|ks': belopp } },  // signerat: kredit < 0
 *   verCount, transCount, fel: [varningar]
 * }
 */
export function parsaSie(text) {
  const ut = {
    foretag: '', orgnr: '', rar: [], konton: {}, ks: {},
    manader: {}, verCount: 0, transCount: 0, fel: [],
  };

  const rader = text.split(/\r?\n/);
  let iVer = false;          // inne i ett #VER { … }-block
  let verDatum = '';         // verifikationens datum (fallback för #TRANS)

  for (const rad of rader) {
    const trimmad = rad.trim();
    if (!trimmad) continue;

    if (trimmad === '{') { iVer = true; continue; }
    if (trimmad === '}') { iVer = false; verDatum = ''; continue; }
    if (!trimmad.startsWith('#')) continue;

    const t = tokenisera(trimmad);
    const typ = t[0].v.toUpperCase();

    if (typ === '#FNAMN' && t[1]) ut.foretag = t[1].v;
    else if (typ === '#ORGNR' && t[1]) ut.orgnr = t[1].v;
    else if (typ === '#RAR' && t.length >= 4) {
      ut.rar.push({ ar: Number(t[1].v), start: t[2].v, slut: t[3].v });
    } else if (typ === '#KONTO' && t.length >= 3) {
      ut.konton[t[1].v] = t[2].v;
    } else if (typ === '#OBJEKT' && t.length >= 4 && t[1].v === '1') {
      ut.ks[t[2].v] = t[3].v; // dimension 1 = kostnadsställe
    } else if (typ === '#VER') {
      // #VER serie vernr datum "text" — datumfältet är det första som ser ut som YYYYMMDD
      ut.verCount++;
      verDatum = (t.find((x) => /^\d{8}$/.test(x.v)) || {}).v || '';
    } else if (typ === '#TRANS' && iVer) {
      // #TRANS konto {dims} belopp [transdat] ["text"]
      const konto = t[1] ? t[1].v : '';
      const dim = t[2] && t[2].typ === 'dim' ? t[2].v : '';
      const beloppTok = t[2] && t[2].typ === 'dim' ? t[3] : t[2];
      const belopp = beloppTok ? Number(beloppTok.v.replace(',', '.')) : NaN;
      if (!/^\d{3,4}$/.test(konto) || !isFinite(belopp)) { ut.fel.push(`Ogiltig #TRANS: ${trimmad.slice(0, 80)}`); continue; }
      // transdat = första YYYYMMDD-token efter beloppet, annars ver-datumet
      const efterBelopp = t.slice(t.indexOf(beloppTok) + 1);
      const transdat = (efterBelopp.find((x) => x.typ === 'ord' && /^\d{8}$/.test(x.v)) || {}).v || verDatum;
      if (!/^\d{8}$/.test(transdat)) { ut.fel.push(`#TRANS utan datum: ${trimmad.slice(0, 80)}`); continue; }

      const ym = transdat.slice(0, 6);
      const ks = ksAvDim(dim);
      const nyckel = `${konto}|${ks}`;
      const m = ut.manader[ym] || (ut.manader[ym] = {});
      m[nyckel] = (m[nyckel] || 0) + belopp;
      ut.transCount++;
    }
  }

  // Avrunda till ören-fria kronor och släng nollsaldon
  for (const ym of Object.keys(ut.manader)) {
    for (const k of Object.keys(ut.manader[ym])) {
      const v = Math.round(ut.manader[ym][k]);
      if (v === 0) delete ut.manader[ym][k];
      else ut.manader[ym][k] = v;
    }
    if (!Object.keys(ut.manader[ym]).length) delete ut.manader[ym];
  }
  return ut;
}

// ── KONTO → BUDGETGRUPP (BAS-kontoplanen) ─────────────────────────
// Samma grupper som budgetmodellen. Saldotecken: intäkter är kredit
// (negativa) i SIE — presentationslagret vänder tecken per grupp.
export function kontoGrupp(konto) {
  const n = Number(konto);
  if (n >= 3000 && n <= 3999) return 'intakt';
  if (n >= 4000 && n <= 4999) return 'vk';
  if (n >= 5000 && n <= 6999) return 'ovrigt';
  if (n >= 7000 && n <= 7699) return 'personal';
  if (n >= 7700 && n <= 7899) return 'avskr';
  if (n >= 7900 && n <= 7999) return 'ovrigt';   // övriga rörelsekostnader
  if (n >= 8000 && n <= 8899) return 'finans';
  return null; // 1xxx/2xxx balans, 89xx skatt/resultat — utanför driftsbudgeten
}

// Summera en parsad fil per månad × grupp (för förhandsvisning/verifiering).
// Resultatet är "resultaträknings-tecken": intäkter positiva, kostnader positiva.
export function summeraPerGrupp(manader) {
  const ut = {}; // ym → { intakt, vk, ovrigt, personal, avskr, finans }
  for (const [ym, rader] of Object.entries(manader)) {
    const s = (ut[ym] = { intakt: 0, vk: 0, ovrigt: 0, personal: 0, avskr: 0, finans: 0 });
    for (const [nyckel, belopp] of Object.entries(rader)) {
      const grupp = kontoGrupp(nyckel.split('|')[0]);
      if (!grupp) continue;
      // intäkter: kredit (−) → vänd; kostnader: debet (+) → behåll
      s[grupp] += grupp === 'intakt' ? -belopp : belopp;
    }
    for (const g of Object.keys(s)) s[g] = Math.round(s[g]);
  }
  return ut;
}
